/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { platformMcpRecordAgentConfigurationCopied } from "../funcs/platformMcpRecordAgentConfigurationCopied.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { PlatformMCPOnboardingState } from "../models/components/platformmcponboardingstate.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RecordPlatformMCPAgentConfigurationCopiedRequest,
  RecordPlatformMCPAgentConfigurationCopiedSecurity,
} from "../models/operations/recordplatformmcpagentconfigurationcopied.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RecordPlatformMCPAgentConfigurationCopiedMutationVariables = {
  request?: RecordPlatformMCPAgentConfigurationCopiedRequest | undefined;
  security?: RecordPlatformMCPAgentConfigurationCopiedSecurity | undefined;
  options?: RequestOptions;
};

export type RecordPlatformMCPAgentConfigurationCopiedMutationData =
  PlatformMCPOnboardingState;

export type RecordPlatformMCPAgentConfigurationCopiedMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * recordAgentConfigurationCopied platformMcp
 *
 * @remarks
 * Record that the user copied the displayed Platform MCP configuration or completed an equivalent supported agent-setup action.
 */
export function useRecordPlatformMCPAgentConfigurationCopiedMutation(
  options?: MutationHookOptions<
    RecordPlatformMCPAgentConfigurationCopiedMutationData,
    RecordPlatformMCPAgentConfigurationCopiedMutationError,
    RecordPlatformMCPAgentConfigurationCopiedMutationVariables
  >,
): UseMutationResult<
  RecordPlatformMCPAgentConfigurationCopiedMutationData,
  RecordPlatformMCPAgentConfigurationCopiedMutationError,
  RecordPlatformMCPAgentConfigurationCopiedMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRecordPlatformMCPAgentConfigurationCopiedMutation(client, options),
    ...options,
  });
}

export function mutationKeyRecordPlatformMCPAgentConfigurationCopied(): MutationKey {
  return ["@gram/client", "platformMcp", "recordAgentConfigurationCopied"];
}

export function buildRecordPlatformMCPAgentConfigurationCopiedMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RecordPlatformMCPAgentConfigurationCopiedMutationVariables,
  ) => Promise<RecordPlatformMCPAgentConfigurationCopiedMutationData>;
} {
  return {
    mutationKey: mutationKeyRecordPlatformMCPAgentConfigurationCopied(),
    mutationFn: function recordPlatformMCPAgentConfigurationCopiedMutationFn({
      request,
      security,
      options,
    }): Promise<RecordPlatformMCPAgentConfigurationCopiedMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(platformMcpRecordAgentConfigurationCopied(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
